import { strokesToSvg } from '../../domain/ink';
import type { InkStroke } from '../../domain/types';
import { deleteHandwriting, logHandwriting } from '../../persistence/repos';
import { getStore } from '../../persistence/store';
import { type LogScope, saveText } from '../../platform/storage';
import { currentFileSubject, useAppStore } from '../../store/useAppStore';

/**
 * Sichert die Handschriftseite eines Kommentars — herausgelöst aus `CommentSheet.tsx`, damit
 * sich der Entscheid „schreiben oder nicht" ohne React testen lässt (das Projekt hat keine
 * Infrastruktur zum Rendern von Komponenten in Tests, `vitest.config.ts` läuft mit
 * `environment: 'node'`).
 */

/**
 * Inhaltlicher Vergleich zweier Strichlisten — keine Objektgleichheit: `addHandwritingStroke`
 * und `clearHandwriting` (`store/useAppStore.ts`) legen bei jeder Änderung ein neues Array an,
 * auch wenn der Inhalt am Ende derselbe wäre. `JSON.stringify` genügt, weil `InkStroke`/
 * `InkPoint` nur Zahlen enthalten — dieselbe Technik wie beim Diff in `repos.ts::saveAssessment`.
 */
export function strokesGleich(a: InkStroke[], b: InkStroke[]): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/**
 * Schreibt die Handschriftseite als SVG (`domain/ink.ts`) — keine `Media`-Zeile: `Media`
 * beschreibt Prüfungsinhalt, den die App nicht schreibt (dieselbe Begründung wie bei Foto,
 * Audio und Video, `MediaTabs.tsx`).
 *
 * `vorher` ist der Stand, mit dem der Prüfer die Fläche vorgefunden hat (beim Öffnen des
 * Kommentarblatts), `nachher` der beim Schließen. Sind beide inhaltlich gleich, geschieht
 * nichts — ohne diese Wache legte jedes bloße Ansehen und Schließen ohne eine einzige Änderung
 * eine neue SVG-Datei an und (sobald `logHandwriting` über einen echten `TaskIndex` verfügt)
 * einen weiteren `IMAGE_COMMENT`-Eintrag auf eine inhaltlich identische Datei — bei einer
 * anhängenden Protokolltabelle auf einem Gerät während einer Prüfung kein Schönheitsfehler.
 *
 * Ein Leeren der geöffneten Seite *ist* eine Änderung und wird deshalb nicht von der Wache
 * verschluckt: `nachher.length === 0` (bei tatsächlich unterschiedlichem `vorher`) **löscht**
 * die vorhandene Datei, statt eine neue, leere zu schreiben — sonst bliebe die zuletzt
 * beschriebene Datei die „jüngste" im Ordner und käme beim nächsten Öffnen unverändert zurück
 * (`repos.ts::loadAssessment` liest immer die jüngste).
 *
 * Der `ExamLog`-Eintrag entsteht unmittelbar nach dem Schreiben, nicht später aus einem
 * Assessment-Diff heraus: `logHandwriting` bekommt den Dateinamen direkt von `saveText`, und
 * `await` erzwingt damit die Reihenfolge „erst Datei, dann Eintrag" durch den Programmablauf.
 * Der `TaskIndex` kommt aus dem Store, **nicht** aus einem eigenen `buildTaskIndex`-Aufruf:
 * `useAppStore` führt ihn ohnehin und tauscht ihn synchron mit `examId` (in *einem* `set()`,
 * s. `applyHydration` und `selectExam`) — die beiden können also nicht auseinanderlaufen.
 * Genau dieselbe Begründung steht schon an der Verdrahtung von `saveAssessment` in `main.tsx`;
 * ihn hier ein zweites Mal aus der Ablage aufzubauen war eine zweite Quelle für dieselbe
 * Wahrheit und kostete bei jedem Schließen des Kommentarblatts einen vollständigen Durchlauf
 * über ExamPart → Container → Item → Frage → Antwort.
 *
 * Kennt der `TaskIndex` die Aufgabe nicht (die Prüfung ist noch nicht in die Ablage
 * importiert), bleibt es beim Schreiben der Datei; `logHandwriting` tut dann bewusst nichts,
 * statt zu werfen.
 */
export async function sichereHandschrift(
  zielId: string,
  vorher: InkStroke[],
  nachher: InkStroke[],
  /** `station-comment` schreibt die Seite an die Station statt an eine Aufgabe. */
  bezug: LogScope = 'task-comment',
): Promise<void> {
  if (strokesGleich(vorher, nachher)) return;

  const anStation = bezug === 'station-comment';
  const taskId = zielId;
  const subject = currentFileSubject();
  const s = useAppStore.getState();
  const grund = {
    examID: subject.examId,
    candidateID: subject.candidateId,
    examinerID: subject.examinerId,
    actorID: s.actorId,
  };
  if (nachher.length === 0) {
    await deleteHandwriting(subject.candidateId, zielId, anStation);
    /*
     * ⚠️ **Auch das Leeren wird protokolliert** (seit 2026-09-14). Vorher blieb es beim
     * Löschen der Datei — die alte Zeile stand aber womöglich schon am Server und zeigte dort
     * für immer auf eine Seite, die es nicht mehr gab. Derselbe Fehler wie bei einer
     * weggeräumten Aufnahme (`persistence/repos.ts::saveAssessment`), und er fällt mit
     * derselben Änderung weg: `0` ist ein gültiger Stand, den der Server als jüngsten sieht.
     */
    await logHandwriting(getStore(), s.taskIndex, grund, taskId, 0, anStation);
    return;
  }
  // ⚠️ Bei einem Stationskommentar **keine** `taskId` in den Dateinamen: sie unterscheidet
  // dort nichts und machte die Datei für `alleDateien` zu einem Aufgabenkommentar.
  await saveText(
    bezug, 'handwriting',
    anStation ? { ...subject, taskId: undefined } : { ...subject, taskId },
    'svg', strokesToSvg(nachher),
  );
  /*
   * ⚠️ **Die Seitenzahl, nicht der Pfad** (seit 2026-09-14, s. `domain/medienzahl.ts`). Eine
   * Aufgabe trägt genau **eine** Handschriftseite — `nachher` ist ihre Strichliste, nicht eine
   * Liste von Seiten. Der Server ordnet die Datei selbst zu, indem er die übertragenen Anhänge
   * scannt; der Rückgabewert von `saveText` wird deshalb nicht mehr gebraucht.
   *
   * Das `await` davor bleibt trotzdem maßgeblich: es erzwingt die Reihenfolge „erst Datei,
   * dann Eintrag" durch den Programmablauf, statt sie zu erhoffen.
   */
  await logHandwriting(getStore(), s.taskIndex, grund, taskId, 1, anStation);
}
